import { apply, burst, celebrate, give, has, onceToday, take, toast } from '../core/effects';
import { S } from '../core/game';
import { dayId } from '../core/state';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say as speak, VOICES } from '../audio/speech';
import { say } from '../ui/dialog';
import { openShop } from '../ui/inventory';
import { openNews } from '../ui/news';
import { openArcade } from '../ui/arcade';
import type { ObjectId } from '../world/mapdef';
import { advance, canStart, completeQuest, isActive, isDone, KITTEN_BUSHES, startQuest, step } from './quests';
import { resetLapClock } from './guide';
import { openBusStop } from './bus';

const min = (n: number) => m().common.minutes(n);
const already = () => toast(m().common.alreadyToday);

/** Handlers for the objects added with the town and the bigger school. */
export const EXTRA_OBJECTS: Partial<Record<ObjectId, () => void>> = {
  libShelf() {
    const t = m().library;
    say({
      who: m().objects.libShelf,
      icon: '📚',
      text: t.shelfText,
      choices: [
        {
          text: t.read,
          cost: min(20),
          run: () => {
            if (!onceToday('libRead')) return already();
            sfx('ok');
            apply({ study: 10, stars: 3, minutes: 20 });
            toast(m().library.readToast);
          },
        },
      ],
    });
  },

  atlasDesk() {
    const t = m().library;
    if (isActive('atlas') && step('atlas') === 1) {
      sfx('pickup');
      give('atlas', 1);
      advance('atlas', 2);
      say({ who: m().objects.atlasDesk, icon: '🗺️', text: t.found });
      return;
    }
    say({ who: m().objects.atlasDesk, icon: '🪑', text: t.deskNothing });
  },

  vending() {
    const t = m().vending;
    say({
      who: m().objects.vending,
      icon: '🥤',
      text: t.text,
      choices: [
        {
          text: `${t.buy} (10🪙)`,
          run: () => {
            if (S().coins < 10) return toast(m().shop.poor);
            apply({ coins: -10 }, { float: false });
            sfx('drop');
            give('juice', 1);
            toast(m().vending.bought);
          },
        },
      ],
    });
  },

  canteen() {
    const t = m().canteen;
    const s = S();
    const open = s.time >= 11 * 60 + 30 && s.time < 13 * 60 + 30;
    say({
      who: m().objects.canteen,
      icon: '🍱',
      text: open ? t.text : t.closed,
      choices: open
        ? [
            {
              text: t.lunch,
              cost: min(25),
              run: () => {
                if (!onceToday('lunch')) return already();
                celebrate();
                apply({ energy: 30, mood: 10, minutes: 25 });
                toast(m().canteen.lunchDone);
              },
            },
          ]
        : [],
    });
  },

  busStop: () => openBusStop('busStop'),
  busStopW: () => openBusStop('busStopW'),
  busStopE: () => openBusStop('busStopE'),

  mailbox() {
    const t = m().mailbox;
    if (onceToday('mail')) {
      sfx('ok');
      apply({ mood: 6 });
      say({ who: m().objects.mailbox, icon: '📮', text: t.postcard });
    } else say({ who: m().objects.mailbox, icon: '📮', text: t.empty });
  },

  pond() {
    const t = m().pond;
    say({
      who: m().objects.pond,
      icon: '🐟',
      text: t.text,
      choices: [
        {
          text: t.feed,
          run: () => {
            if (!onceToday('fish')) return already();
            sfx('water');
            burst('drop', 8);
            apply({ mood: 8, minutes: 5 });
            toast(m().pond.feedToast);
          },
        },
      ],
    });
  },

  bush1: () => bush(0),
  bush2: () => bush(1),
  bush3: () => bush(2),

  kiteTree() {
    const t = m().kite;
    if (isActive('kite') && step('kite') === 1) {
      const s = S();
      const q = s.quests.kite!;
      q.data = (q.data ?? 0) + 1;
      sfx('rustle');
      burst('sparkle', 4);
      if (q.data >= 3) {
        sfx('pickup');
        give('kite', 1);
        advance('kite', 2);
        say({ who: m().objects.kiteTree, icon: '🪁', text: t.fell });
      } else toast(t.shakeMore(3 - q.data));
      return;
    }
    say({ who: m().objects.kiteTree, icon: '🌳', text: isDone('kite') ? t.treeAfter : t.treeText });
  },

  swing() {
    const t = m().swing;
    say({
      who: m().objects.swing,
      icon: '🛝',
      text: t.text,
      choices: [{ text: t.play, cost: min(10), run: () => (sfx('ok'), apply({ mood: onceToday('swing') ? 10 : 3, energy: -3, minutes: 10 }), toast(m().swing.toast)) }],
    });
  },

  fridge() {
    const t = m().fridge;
    say({
      who: m().objects.fridge,
      icon: '🧊',
      text: t.text,
      choices: [{ text: t.snack, run: () => (onceToday('snack') ? (sfx('pop'), apply({ energy: 12, mood: 4 }), toast(m().fridge.snackToast)) : already()) }],
    });
  },

  tv() {
    const t = m().tv;
    say({
      who: m().objects.tv,
      icon: '📺',
      text: t.text,
      choices: [
        { text: `📰 ${m().news.title}`, run: () => (apply({ minutes: 15 }, { float: false }), openNews()) },
        { text: m().arcade.title, run: openArcade },
        { text: t.watch, cost: min(30), run: () => (sfx('ok'), apply({ mood: onceToday('tv') ? 10 : 2, minutes: 30 }), toast(m().tv.toast)) },
      ],
    });
  },

  shopCounter() {
    talkExtra('clerk');
  },
};

function bush(i: number): void {
  const t = m().kitten;
  const s = S();
  sfx('rustle');
  if (isActive('kitten') && step('kitten') === 1 && KITTEN_BUSHES[s.quests.kitten!.data ?? 0] === KITTEN_BUSHES[i]) {
    sfx('pickup');
    give('kitten', 1);
    advance('kitten', 2);
    burst('heart', 6);
    say({ who: m().objects.bush, icon: '🐱', text: t.found });
    return;
  }
  say({ who: m().objects.bush, icon: '🌿', text: isActive('kitten') ? t.notHere : m().bush.text });
}

/** NPC conversations added with the town. Returns false if the id isn't handled here. */
export function talkExtra(id: string): boolean {
  switch (id) {
    case 'librarian':
      return librarian(), true;
    case 'rain':
      return rain(), true;
    case 'coach':
      return coach(), true;
    case 'clerk':
      return clerk(), true;
    case 'grandma':
      return grandma(), true;
    case 'bo':
      return bo(), true;
    case 'cat':
      return cat(), true;
  }
  return false;
}

function librarian(): void {
  const t = m().library;
  const who = m().npc.librarian;
  if (canStart('atlas')) {
    say({
      who,
      icon: '👵',
      text: t.ask,
      choices: [
        { text: t.accept, run: () => (startQuest('atlas'), say({ who: m().npc.librarian, icon: '👵', text: m().library.hint })) },
        { text: m().common.no },
      ],
    });
    return;
  }
  if (isActive('atlas') && step('atlas') === 2 && take('atlas')) {
    completeQuest('atlas');
    say({ who, icon: '👵', text: t.thanks });
    return;
  }
  say({ who, icon: '👵', text: isActive('atlas') ? t.hint : t.hello });
}

function rain(): void {
  const t = m().rain;
  const who = m().npc.rain;
  if (dayId(S()) === 'sat') return say({ who, icon: '👧', text: t.sat });
  if (canStart('study')) {
    say({ who, icon: '👧', text: t.offer, choices: [{ text: t.accept, run: () => startQuest('study') }, { text: m().common.no }] });
    return;
  }
  if (isActive('study') && step('study') === 2) {
    completeQuest('study');
    say({ who, icon: '👧', text: t.done });
    return;
  }
  say({ who, icon: '👧', text: isActive('study') ? t.waiting(S().counters.wordsToday ?? 0) : t.hello });
}

function coach(): void {
  const t = m().coach;
  const who = m().npc.coach;
  const s = S();
  const startRun = () => {
    if (!s.quests.laps) startQuest('laps');
    else advance('laps', 1);
    s.quests.laps!.data = 0;
    resetLapClock();
    sfx('fanfare');
    toast(m().coach.go);
  };
  if (canStart('laps') || (isActive('laps') && step('laps') === 0)) {
    say({ who, icon: '🏃', text: isActive('laps') ? t.retry : t.offer, choices: [{ text: t.accept, run: startRun }, { text: m().common.no }] });
    return;
  }
  if (isActive('laps') && step('laps') === 2) {
    completeQuest('laps');
    say({ who, icon: '🏃', text: t.done });
    return;
  }
  say({ who, icon: '🏃', text: isActive('laps') ? t.running : isDone('laps') ? t.after : t.hello });
}

function clerk(): void {
  const t = m().clerk;
  say({
    who: m().npc.clerk,
    icon: '🧑‍💼',
    text: t.lines[Math.floor(Math.random() * t.lines.length)],
    choices: [{ text: t.shop, run: openShop }, { text: m().common.cancel }],
  });
}

function grandma(): void {
  const t = m().grandma;
  const who = m().npc.grandma;
  const s = S();
  if (isActive('letter') && step('letter') === 1 && take('letter')) {
    completeQuest('letter');
    say({ who, icon: '👵', text: t.letterThanks });
    return;
  }
  if (isActive('kitten') && step('kitten') === 2 && take('kitten')) {
    completeQuest('kitten');
    burst('heart', 10);
    speak(t.kittenThanks, VOICES.mom);
    say({ who, icon: '👵', text: t.kittenThanks });
    return;
  }
  if (canStart('kitten')) {
    say({
      who,
      icon: '👵',
      text: t.kittenAsk,
      choices: [
        {
          text: t.kittenAccept,
          run: () => startQuest('kitten', Math.floor(Math.random() * 3)),
        },
        { text: m().common.no },
      ],
    });
    return;
  }
  const lines = isActive('kitten') ? [t.kittenWaiting] : t.lines;
  say({ who, icon: '👵', text: lines[Math.floor(Math.random() * lines.length)] });
  void s;
}

function bo(): void {
  const t = m().kite;
  const who = m().npc.bo;
  if (canStart('kite')) {
    say({ who, icon: '👦', text: t.ask, choices: [{ text: t.accept, run: () => startQuest('kite', 0) }, { text: m().common.no }] });
    return;
  }
  if (isActive('kite') && step('kite') === 2 && take('kite')) {
    completeQuest('kite');
    say({ who, icon: '👦', text: t.thanks });
    return;
  }
  say({ who, icon: '👦', text: isActive('kite') ? t.waiting : isDone('kite') ? t.after : t.hello });
}

function cat(): void {
  const lines = m().cat.lines;
  sfx('pop');
  burst('heart', 3);
  if (onceToday('cat')) apply({ mood: 5 }, { float: false });
  toast(lines[Math.floor(Math.random() * lines.length)]);
}

/** Extra options that existing characters gain for the new quests. */
export const questOptions = {
  chunbo(): { text: string; run: () => void }[] {
    const t = m().chunbo;
    if (canStart('riceBall')) return [{ text: t.hungryOpt, run: () => say({ who: m().npc.chunbo, icon: '👮', text: t.hungry, choices: [{ text: t.riceAccept, run: () => startQuest('riceBall') }, { text: m().common.no }] }) }];
    if (isActive('riceBall') && has('riceBall'))
      return [
        {
          text: t.riceGive,
          run: () => {
            take('riceBall');
            completeQuest('riceBall');
            say({ who: m().npc.chunbo, icon: '👮', text: t.riceThanks });
          },
        },
      ];
    return [];
  },
  emma(): { text: string; run: () => void }[] {
    if (!(isActive('xiaoyuPicnic') && step('xiaoyuPicnic') === 1)) return [];
    const t = m().xiaoyu;
    return [
      {
        text: t.askEmma,
        run: () => {
          completeQuest('xiaoyuPicnic');
          say({ who: m().npc.emma, icon: '👱‍♀️', text: t.emmaYes });
        },
      },
    ];
  },
  mom(): { text: string; run: () => void }[] {
    const t = m().mom;
    if (!canStart('letter')) return [];
    return [
      {
        text: t.helpOpt,
        run: () => {
          give('letter', 1);
          startQuest('letter');
          say({ who: m().npc.mom, icon: '👩', text: t.letter });
        },
      },
    ];
  },
};

/** Xiaoyu on Saturday, sitting alone in the park. */
export function xiaoyuSaturday(): void {
  const t = m().xiaoyu;
  const who = m().xiaoyu.title;
  if (isDone('xiaoyuPicnic')) return say({ who, icon: '🧑', text: t.satJoined });
  if (canStart('xiaoyuPicnic')) {
    say({
      who,
      icon: '🧑',
      text: t.satHello,
      choices: [
        { text: t.satInvite, run: () => (startQuest('xiaoyuPicnic'), say({ who: m().xiaoyu.title, icon: '🧑', text: m().xiaoyu.satAccept })) },
        { text: m().common.no },
      ],
    });
    return;
  }
  say({ who, icon: '🧑', text: t.satAccept });
}
